import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';

import { LatencyCard, TOOLTIP_STYLE, TOOLTIP_LABEL_STYLE } from './LatencyCard';
import { CHART_STYLES } from '../../Dashboard/constants/chartStyles';
import { timeAxisFormat, type LatencyPeriod } from '../const';
import type { ThroughputSeriesResponse } from '../../../../../api';

// Distinct colors for the per-model lines (cycled if there are more models).
const PALETTE = [
  '#6366f1',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#14b8a6',
  '#f97316',
  '#64748b',
];

const modelLabel = (m: string): string => m || '—';

const fmtCount = (v: number): string => Math.round(v).toLocaleString();

type TooltipEntry = { name?: string; value?: number; color?: string };

const RequestsTooltip: React.FC<{
  active?: boolean;
  label?: React.ReactNode;
  payload?: TooltipEntry[];
}> = ({ active, label, payload }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  return (
    <div style={TOOLTIP_STYLE}>
      {label !== undefined && label !== null && label !== '' && (
        <div style={TOOLTIP_LABEL_STYLE}>{label}</div>
      )}
      {payload.map((entry, index) => (
        <div key={index} style={{ color: entry.color ?? '#111827' }}>
          {entry.name}: {fmtCount(Number(entry.value))} req
        </div>
      ))}
    </div>
  );
};

type Props = {
  data?: ThroughputSeriesResponse | null;
  loading?: boolean;
  period?: LatencyPeriod;
};

export const ThroughputByModelCard: React.FC<Props> = ({
  data,
  loading,
  period,
}) => {
  const tickFormat = timeAxisFormat(period);
  const series = data?.series;

  // Pivot the per-model series into one row per time bucket with a column per
  // model, which is what recharts' shared X-axis needs.
  const { rows, models } = useMemo(() => {
    const list = series ?? [];
    const byTs = new Map<string, Record<string, number | string>>();
    list.forEach(s => {
      const key = modelLabel(s.model);
      s.points.forEach(p => {
        const row = byTs.get(p.timestamp) ?? { __ts: p.timestamp };
        row[key] = p.count;
        byTs.set(p.timestamp, row);
      });
    });
    const pivoted = Array.from(byTs.values())
      .sort((a, b) => String(a.__ts).localeCompare(String(b.__ts)))
      .map(r => ({
        ...r,
        name: format(new Date(String(r.__ts)), tickFormat),
      }));
    return { rows: pivoted, models: list.map(s => modelLabel(s.model)) };
  }, [series, tickFormat]);

  return (
    <LatencyCard
      title="Throughput by model — requests over time"
      subtitle="requests per time bucket, per model, for the selected type"
      loading={loading}
      isEmpty={rows.length === 0}
      height="h-[440px]"
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={rows}
          margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={CHART_STYLES.COLORS.GRID}
          />
          <XAxis
            dataKey="name"
            stroke={CHART_STYLES.COLORS.AXIS}
            fontSize={11}
            tickLine={false}
            axisLine={false}
            minTickGap={24}
          />
          <YAxis
            stroke={CHART_STYLES.COLORS.AXIS}
            fontSize={12}
            tickLine={false}
            axisLine={false}
            width={48}
            allowDecimals={false}
            tickFormatter={(value: number) => fmtCount(value)}
          />
          <Tooltip
            cursor={{ stroke: CHART_STYLES.COLORS.GRID }}
            isAnimationActive={false}
            content={<RequestsTooltip />}
          />
          <Legend />
          {models.map((model, index) => (
            <Line
              key={model}
              type="monotone"
              dataKey={model}
              name={model}
              stroke={PALETTE[index % PALETTE.length]}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </LatencyCard>
  );
};
